import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, Modal, Row, Col } from 'react-bootstrap';
import { CustomButton } from '../../pages/helpDesk/styles';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import enumUserRole from '../../enum/enumUserRole';

const ModalProfile = ({ isOpen, onClose, id }) => {
  const { currentUser, refreshCurrentUser } = useContext(ContextUser);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState('');
  const [phoneValue, setPhoneValue] = useState('');

  const getRoleDescription = (roleId) => {
    const roleMap = {
      [enumUserRole.ADMIN]: 'Administrador',
      [enumUserRole.ACCESS_ORG_LEVEL]: 'Acesso Nível Organização',
      [enumUserRole.ACCESS_BRANCH_LEVEL]: 'Acesso Nível Secretaria',
      [enumUserRole.ACCESS_UNIT_LEVEL]: 'Acesso Nível Unidade',
      [enumUserRole.ACCESS_DEPARTMENT_LEVEL]: 'Acesso Nível Departamento',
    };
    return roleMap[roleId] || 'Não informado';
  };

  // Função para obter as iniciais do nome
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const applyPhoneMask = (value) => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara conforme o usuário digita
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2)}`;
    } else if (numbers.length <= 10) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 6)}-${numbers.substring(6)}`;
    } else {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7, 11)}`;
    }
  };

  const formatCPF = (cpf) => {
    if (!cpf || cpf === 'Não informado') return cpf;
    
    // Remove caracteres não numéricos
    const numbers = cpf.replace(/\D/g, '');
    
    // Aplica a máscara do CPF: 000.000.000-00
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.substring(0, 3)}.${numbers.substring(3)}`;
    } else if (numbers.length <= 9) {
      return `${numbers.substring(0, 3)}.${numbers.substring(3, 6)}.${numbers.substring(6)}`;
    } else {
      return `${numbers.substring(0, 3)}.${numbers.substring(3, 6)}.${numbers.substring(6, 9)}-${numbers.substring(9, 11)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const maskedValue = applyPhoneMask(e.target.value);
    setPhoneValue(maskedValue);
  };

  useEffect(() => {
    if (currentUser) {
      // Separar nome completo em first name e last name
      const fullName = currentUser.name || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Combinar DDD e número em um único campo de telefone formatado
      const phone = currentUser.phone || {};
      const phoneDDD = phone.ddd || '';
      const phoneNumber = phone.number || '';
      
      let fullPhone = '';
      if (phoneDDD && phoneNumber) {
        // Formatar como (11) 99999-9999
        if (phoneNumber.length === 9) {
          fullPhone = `(${phoneDDD}) ${phoneNumber.substring(0, 5)}-${phoneNumber.substring(5)}`;
        } else if (phoneNumber.length === 8) {
          fullPhone = `(${phoneDDD}) ${phoneNumber.substring(0, 4)}-${phoneNumber.substring(4)}`;
        } else {
          fullPhone = `(${phoneDDD}) ${phoneNumber}`;
        }
      }

      reset({
        firstName: firstName,
        lastName: lastName,
        phone: fullPhone,
      });
      setPhoneValue(fullPhone);
      setPreviewUrl(currentUser.imageUrl || null);
      setImageError('');
    }
  }, [currentUser, reset, isOpen]);

  const onSubmit = async (data) => {
    try {
      // Validar telefone antes de enviar
      if (!phoneValue || !/^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(phoneValue)) {
        toast.error('Por favor, digite um telefone válido no formato (11) 99999-9999');
        return;
      }

      // Combinar first name e last name em um nome completo
      const fullName = `${data.firstName} ${data.lastName}`.trim();

      // Separar telefone em DDD e número usando o valor mascarado
      let phoneDDD = '';
      let phoneNumber = '';
      
      if (phoneValue) {
        const phoneDigits = phoneValue.replace(/\D/g, ''); // Remove caracteres não numéricos
        
        if (phoneDigits.length === 10) {
          // Formato: 1199999999 (DDD + 8 dígitos)
          phoneDDD = phoneDigits.substring(0, 2);
          phoneNumber = phoneDigits.substring(2);
        } else if (phoneDigits.length === 11) {
          // Formato: 11999999999 (DDD + 9 dígitos)
          phoneDDD = phoneDigits.substring(0, 2);
          phoneNumber = phoneDigits.substring(2);
        } else if (phoneDigits.length >= 8 && phoneDigits.length <= 9) {
          // Apenas número sem DDD
          phoneNumber = phoneDigits;
        }
      }

      // Preparar dados para envio
      const formData = new FormData();
      formData.append('name', fullName);
      formData.append('phoneDDD', phoneDDD);
      formData.append('phoneNumber', phoneNumber);
      
      // Adicionar imagem se houver
      if (imageFile) {
        formData.append('image', imageFile);
      }

      // Usar o novo endpoint de perfil
      await api.patch(`/users/${currentUser.id}/profile`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await refreshCurrentUser?.();
      toast.success('Perfil atualizado com sucesso.');
      onClose?.();
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      toast.error('Não foi possível atualizar seu perfil.');
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        const msg = 'Arquivo excede 2MB. Selecione uma imagem menor.';
        setImageError(msg);
        toast.error(msg);
        // limpa seleção
        event.target.value = '';
        return;
      }
      setImageError('');
      setImageFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleResetPassword = async () => {
    try {
      await api.post('/auth/forgot_password', { email: currentUser.email });
      toast.info('Enviamos um e-mail para redefinir sua senha.');
    } catch (error) {
      toast.error('Não foi possível enviar o e-mail de redefinição.');
    }
  };

  return (
    <Modal id={id || 'modal-profile'} show={isOpen} onHide={onClose} centered size='lg'>
      <Modal.Header id='modal-profile-header' closeButton>
        Meu perfil
      </Modal.Header>
      <Modal.Body id='modal-profile-body'>
        <Form id='modal-profile-form' onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12} className='mb-3'>
              <div id='modal-profile-avatar-preview' style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {previewUrl ? (
                  <img
                    id='modal-profile-image-preview'
                    src={previewUrl}
                    alt='Pré-visualização da foto'
                    width={96}
                    height={96}
                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    id='modal-profile-avatar-fallback'
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: '50%',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px',
                      fontWeight: 'bold'
                    }}
                  >
                    {getInitials(currentUser?.name)}
                  </div>
                )}
                <div id='modal-profile-image-actions'>
                  <input id='modal-profile-image-file' type='file' accept='image/*' onChange={handleImageChange} style={{ display: 'none' }} />
                  <CustomButton id='modal-profile-image-button' type='button' onClick={() => document.getElementById('modal-profile-image-file').click()}>
                    Selecione a foto do perfil
                  </CustomButton>
                  <div id='modal-profile-image-hint' style={{ marginTop: 6, fontSize: 12, color: '#6c757d' }}>
                    Tamanho máximo permitido: 2MB. Formatos aceitos: JPG, PNG, WEBP.
                  </div>
                  {imageError && (
                    <div id='modal-profile-image-error' style={{ marginTop: 4, fontSize: 12, color: '#dc3545' }}>
                      {imageError}
                    </div>
                  )}
                </div>
              </div>
            </Col>

            {/* Campos Editáveis */}
            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-first-name'>
                <Form.Label id='modal-profile-first-name-label'>Nome</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Seu nome'
                  {...register('firstName', { required: 'Informe seu nome' })}
                  isInvalid={!!errors.firstName}
                />
                <Form.Control.Feedback type='invalid' id='modal-profile-first-name-error'>
                  {errors.firstName?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-last-name'>
                <Form.Label id='modal-profile-last-name-label'>Sobrenome</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Seu sobrenome'
                  {...register('lastName')}
                  isInvalid={!!errors.lastName}
                />
                <Form.Control.Feedback type='invalid' id='modal-profile-last-name-error'>
                  {errors.lastName?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-phone'>
                <Form.Label id='modal-profile-phone-label'>Telefone</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='(11) 99999-9999'
                  value={phoneValue}
                  onChange={handlePhoneChange}
                  maxLength={15}
                  isInvalid={!phoneValue || !/^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(phoneValue)}
                />
                <Form.Control.Feedback type='invalid' id='modal-profile-phone-error'>
                  {!phoneValue ? 'Telefone é obrigatório' : 'Formato: (11) 99999-9999'}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Campos Não Editáveis */}
            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-email'>
                <Form.Label id='modal-profile-email-label'>E-mail</Form.Label>
                <Form.Control
                  type='email'
                  value={currentUser?.email || ''}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </Form.Group>
            </Col>

            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-internal-code'>
                <Form.Label id='modal-profile-internal-code-label'>Código Interno</Form.Label>
                <Form.Control
                  type='text'
                  value={currentUser?.internalCod || 'Não informado'}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </Form.Group>
            </Col>

            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-job-title'>
                <Form.Label id='modal-profile-job-title-label'>Cargo</Form.Label>
                <Form.Control
                  type='text'
                  value={currentUser?.jobTitle || 'Não informado'}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </Form.Group>
            </Col>

            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-doc-number'>
                <Form.Label id='modal-profile-doc-number-label'>CPF</Form.Label>
                <Form.Control
                  type='text'
                  value={formatCPF(currentUser?.document) || 'Não informado'}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </Form.Group>
            </Col>

            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-role'>
                <Form.Label id='modal-profile-role-label'>Perfil</Form.Label>
                <Form.Control
                  type='text'
                  value={getRoleDescription(currentUser?.roleId)}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </Form.Group>
            </Col>

            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-created-at'>
                <Form.Label id='modal-profile-created-at-label'>Data de Cadastro</Form.Label>
                <Form.Control
                  type='text'
                  value={currentUser?.createdAt ? new Date(currentUser.createdAt).toLocaleDateString('pt-BR') : 'Não informado'}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </Form.Group>
            </Col>

            <Col xs={6} className='mb-3'>
              <Form.Group controlId='modal-profile-org-ref'>
                <Form.Label id='modal-profile-org-ref-label'>Organização</Form.Label>
                <Form.Control
                  type='text'
                  value={currentUser?.orgRef || 'Não informado'}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} className='mb-2 d-flex justify-content-end'>
              <CustomButton id='modal-profile-reset-password' type='button' onClick={handleResetPassword}>
                Redefinir senha
              </CustomButton>
            </Col>
          </Row>
          <div id='modal-profile-actions' className='d-flex justify-content-between mt-3'>
            <CustomButton id='modal-profile-cancel' type='button' onClick={onClose}>
              Cancelar
            </CustomButton>
            <Button id='modal-profile-save' type='submit' disabled={isSubmitting || !!imageError}>
              Salvar
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ModalProfile;


